import { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { useSelector } from 'react-redux';
import { selectCurrentPlan, selectSubscriptionLoading } from '@store/slices/subscriptionSlice';
import { ROUTES, USER_ROLES } from '@constants';
import LoadingSpinner from './LoadingSpinner';
import PlanUpgradeModal from './PlanUpgradeModal';

const ProtectedRoute = ({ children, roles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  const currentPlan = useSelector(selectCurrentPlan);
  const planLoading = useSelector(selectSubscriptionLoading);
  const [showPlanModal, setShowPlanModal] = useState(false);

  // Student plan check - show modal if no active plan
  // MUST be before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (user?.role === USER_ROLES.STUDENT && !planLoading && !currentPlan) {
      setShowPlanModal(true);
    }
  }, [user, currentPlan, planLoading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // CRITICAL: If authenticated but user data is not yet loaded, wait for it
  // This prevents premature 401 redirect when page is refreshed
  // This is especially important for admin routes after page refresh
  if (isAuthenticated && !user) {
    console.log('ProtectedRoute: Authenticated but user data not loaded yet, waiting...', {
      isAuthenticated,
      hasUser: !!user,
      requiredRoles: roles
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check if user has required role
  if (roles.length > 0 && user && !roles.includes(user.role)) {
    console.error('ProtectedRoute: Role mismatch', {
      userRole: user.role,
      requiredRoles: roles
    });
    return <Navigate to={ROUTES.UNAUTHORIZED} replace />;
  }

  // Show plan selection modal for students without active plan
  if (user?.role === USER_ROLES.STUDENT && !planLoading && !currentPlan && showPlanModal) {
    return (
      <>
        <div className="filter blur-sm pointer-events-none min-h-screen">
          {children}
        </div>
        <PlanUpgradeModal 
          isOpen={true} 
          onClose={() => {}} 
          mandatory={true}
        />
      </>
    );
  }

  // Mentor verification flow - use isDocumentsApproved flag for instant routing
  if (user?.role === USER_ROLES.MENTOR) {
    const currentPath = location.pathname;
    
    // Allow access to verification flow pages
    const verificationPages = [
      ROUTES.COMPLETE_PROFILE,
      ROUTES.PROFILE_VERIFICATION,
      ROUTES.PROFILE,
      ROUTES.SETTINGS,
    ];
    
    // If documents are approved, allow dashboard access
    const isApproved = user.isDocumentsApproved || user.adminApproved === 'approved';
    
    // Redirect mentors from /home to /mentor dashboard
    if (currentPath === ROUTES.HOME || currentPath === ROUTES.DASHBOARD) {
      if (isApproved) {
        return <Navigate to={ROUTES.MENTOR_DASHBOARD} replace />;
      } else {
        return <Navigate to={ROUTES.COMPLETE_PROFILE} replace />;
      }
    }
    
    // If trying to access dashboard without approval, redirect to complete profile
    if (currentPath === ROUTES.MENTOR_DASHBOARD && !isApproved) {
      return <Navigate to={ROUTES.COMPLETE_PROFILE} replace />;
    }
    
    // If approved and trying to access verification pages, redirect to mentor dashboard
    if (isApproved && (currentPath === ROUTES.COMPLETE_PROFILE || currentPath === ROUTES.PROFILE_VERIFICATION)) {
      return <Navigate to={ROUTES.MENTOR_DASHBOARD} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
